/*
 * Settings pages added for parity with the old Steward (2026-10-29): Crew and payroll, Alerts, and Reviews and
 * referrals. Switches save the moment they are flipped; words and numbers have their own Edit.
 */
import { Link } from "@tanstack/react-router";
import { formatMoney, SETTINGS_DEFAULTS, toCents, type Cents, type Settings, type SettingsPatch } from "@steward/shared";
import { useState } from "react";
import { useSettings, useUpdateSettings } from "../../api/hooks";
import { useSession } from "../../app/session";
import { shortDate } from "../../components/format";
import { InputText } from "../../components/fields";
import { useToast } from "../../components/overlays";
import { Banner, Button, Card, Details, SettingRow, Skeleton, Switch } from "../../components/parts";
import { errText } from "../../components/work";
import { PayrollEdit, WEEKDAYS } from "./Settings";

/** A switch that saves itself, for owners; others see it greyed. */
function useSaveNow() {
  const save = useUpdateSettings();
  const toast = useToast();
  return {
    busy: save.isPending,
    set: (patch: SettingsPatch, said: string) =>
      save.mutate(patch, { onSuccess: () => toast(said), onError: (e) => toast(errText(e), { kind: "error" }) }),
  };
}

export function TeamSettings() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const now = useSaveNow();
  const [editing, setEditing] = useState(false);
  if (!s.data) return <Skeleton rows={6} />;
  const { settings, own } = s.data;
  const ownPay = (own.payroll ?? {}) as Record<string, unknown>;
  const from = (key: string) => (key in ownPay ? null : <span className="muted"> · from above</span>);
  const owner = can("settings.update");
  const p = settings.payroll;
  return (
    <>
      <Card
        id="payroll"
        title="Payroll"
        action={
          owner ? (
            <Button type="secondary" size="small" icon="edit" onClick={() => setEditing(true)}>
              Edit
            </Button>
          ) : null
        }
        flush
      >
        <Details
          rows={[
            [
              "Pay period",
              <>
                {p.periodWeeks === 2
                  ? `Every two weeks, Monday to Sunday, from ${shortDate(p.twoWeekFrom ?? "2026-01-05")}`
                  : "Every week, Monday to Sunday"}
                {from("periodWeeks")}
              </>,
            ],
            [
              "Payday",
              <>
                {WEEKDAYS[p.paydayWeekday - 1]} after the pay period ends{from("paydayWeekday")}
              </>,
            ],
            [
              "Default pool",
              <>
                {p.defaultPoolPercent === null ? "None" : `${String(p.defaultPoolPercent)}%`}
                {from("defaultPoolPercent")}
              </>,
            ],
            [
              "Business jobs pool",
              <>
                {p.commercialPoolPercent === null ? "None" : `${String(p.commercialPoolPercent)}%`}
                {from("commercialPoolPercent")}
              </>,
            ],
          ]}
        />
      </Card>
      <Card id="crew" title="Crew" flush>
        <SettingRow
          title="Crew take cash or check"
          changed={settings.crew.collectsMoney !== SETTINGS_DEFAULTS.crew.collectsMoney}
          description="When they finish a visit, crew say if the client paid them cash (it comes off their pay), gave a company check, or nothing."
          control={
            <Switch
              label="Crew take cash or check"
              checked={settings.crew.collectsMoney}
              disabled={!owner || now.busy}
              onChange={(v) =>
                now.set({ crew: { collectsMoney: v } }, v ? "Crew can take cash or check" : "Crew never take money")
              }
            />
          }
        />
        <SettingRow
          title="Clock out on their own"
          changed={settings.field.autoClockOut !== SETTINGS_DEFAULTS.field.autoClockOut}
          description="When their last visit of the day is marked done, they are clocked out at that time. Off means they clock out themselves, and a forgotten one shows in Loose ends."
          control={
            <Switch
              label="Clock out on their own"
              checked={settings.field.autoClockOut}
              disabled={!owner || now.busy}
              onChange={(v) => now.set({ field: { autoClockOut: v } }, v ? "Auto clock out on" : "Auto clock out off")}
            />
          }
        />
        <SettingRow
          title="Live GPS"
          changed={settings.field.liveGps !== SETTINGS_DEFAULTS.field.liveGps}
          description="While someone is on the clock, their phone sends where it is every 5 minutes, so they show on the Home map. Off means nothing is kept."
          control={
            <Switch
              label="Live GPS"
              checked={settings.field.liveGps}
              disabled={!owner || now.busy}
              onChange={(v) => now.set({ field: { liveGps: v } }, v ? "Live GPS on" : "Live GPS off")}
            />
          }
        />
      </Card>
      <Card id="people" title="Each person's settings">
        <p className="muted">
          Who sees job prices, who sees My pay, their language and their pay rate are set on each person in Team.
        </p>
        <div>
          <Link to="/hr/team" className="btn secondary small">
            Open Team
          </Link>
        </div>
      </Card>
      {editing ? <PayrollEdit settings={settings} onClose={() => setEditing(false)} /> : null}
    </>
  );
}

const ALERTS: [Exclude<keyof Settings["alerts"], "textTo" | "paidEmail" | "leadEmail" | "emailTo">, string, string][] = [
  ["newRequest", "New request", "Someone asked for work on your website."],
  ["quoteApproved", "Quote approved", "A client approved a quote and it is ready to schedule."],
  ["paid", "Payment came in", "A client paid an invoice online."],
];

export function AlertsSettings() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const now = useSaveNow();
  if (!s.data) return <Skeleton rows={4} />;
  const a = s.data.settings.alerts;
  const owner = can("settings.update");
  return (
    <>
      <Card id="alerts" title="What the bell tells the office" flush>
        {ALERTS.map(([k, title, what]) => (
          <SettingRow
            key={k}
            title={title}
            description={what}
            changed={a[k] !== SETTINGS_DEFAULTS.alerts[k]}
            control={
              <Switch
                label={title}
                checked={a[k]}
                disabled={!owner || now.busy}
                onChange={(v) => now.set({ alerts: { [k]: v } }, `${title} alerts ${v ? "on" : "off"}`)}
              />
            }
          />
        ))}
      </Card>
      <p className="muted">
        Owners, managers and dispatch see these. Crew always hear when one of their own jobs is added, moved or canceled.
      </p>
      <EmailMeCard key={a.emailTo} paid={a.paidEmail} lead={a.leadEmail} saved={a.emailTo} owner={owner} />
      <LeadTextCard key={a.textTo} saved={a.textTo} owner={owner} />
    </>
  );
}

/** The owner's emails: each new website request, and each time an invoice is paid however it was paid. */
function EmailMeCard({ paid, lead, saved, owner }: { paid: boolean; lead: boolean; saved: string; owner: boolean }) {
  const now = useSaveNow();
  const [to, setTo] = useState(saved);
  const ok = to.trim() === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim());
  return (
    <Card id="paid-email" title="Email me">
      <SettingRow
        title="New request email"
        description="Who asked, their phone, address and what they want, with a button to call them."
        changed={lead !== SETTINGS_DEFAULTS.alerts.leadEmail}
        control={
          <Switch
            label="New request email"
            checked={lead}
            disabled={!owner || now.busy}
            onChange={(v) => now.set({ alerts: { leadEmail: v } }, `New request email ${v ? "on" : "off"}`)}
          />
        }
      />
      <SettingRow
        title="Paid invoice email"
        description="Who paid, how much, which invoice and how they paid. Card, bank, cash, check, all of them."
        changed={paid !== SETTINGS_DEFAULTS.alerts.paidEmail}
        control={
          <Switch
            label="Paid invoice email"
            checked={paid}
            disabled={!owner || now.busy}
            onChange={(v) => now.set({ alerts: { paidEmail: v } }, `Paid invoice email ${v ? "on" : "off"}`)}
          />
        }
      />
      <p className="muted">Leave it empty to use your company email.</p>
      <div className="row">
        <InputText
          label="Email"
          inputMode="email"
          value={to}
          disabled={!owner || !(paid || lead)}
          placeholder="you@yourcompany.com"
          onChange={(e) => setTo(e.target.value)}
        />
        <Button
          type="secondary"
          disabled={!owner || !(paid || lead) || !ok || to.trim() === saved || now.busy}
          onClick={() =>
            now.set(
              { alerts: { emailTo: to.trim() } },
              to.trim() ? "Alerts will email this address" : "Alerts will email your company email",
            )
          }
        >
          Save
        </Button>
      </div>
    </Card>
  );
}

/** The owner's text about each new website request, to call back fast (old Steward Request alert). */
function LeadTextCard({ saved, owner }: { saved: string; owner: boolean }) {
  const now = useSaveNow();
  const [to, setTo] = useState(saved);
  const digits = to.replace(/\D/g, "");
  const ok = digits.length === 0 || digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
  return (
    <Card id="lead-text" title="Text me new website requests">
      <p className="muted">
        Each new request from your website comes to this phone with their number, address, what they want and the price they saw,
        so you can call back within 5 minutes. Leave it empty for none.
      </p>
      <div className="row">
        <InputText
          label="Phone"
          inputMode="tel"
          value={to}
          disabled={!owner}
          placeholder="(504) 555-0100"
          onChange={(e) => setTo(e.target.value)}
        />
        <Button
          type="secondary"
          disabled={!owner || !ok || to === saved || now.busy}
          onClick={() =>
            now.set({ alerts: { textTo: to.trim() } }, to.trim() ? "New requests will text this phone" : "Request texts off")
          }
        >
          Save
        </Button>
      </div>
    </Card>
  );
}

type Linked = Pick<
  Settings["marketing"],
  "googleUser" | "googleAdsId" | "googleAdsManagerId" | "metaAdAccountId" | "facebookPageId"
>;

// The Google profile is reached with Sign in with Google on Marketing, Google profile (2026-11-01), not a typed account.
const LINKED_FIELDS: { key: keyof Linked; label: string; placeholder: string; help: string }[] = [
  {
    key: "googleAdsId",
    label: "Google Ads account id",
    placeholder: "123-456-7890",
    help: "Top right in Google Ads. Local Services spend comes from the same account.",
  },
  {
    key: "googleAdsManagerId",
    label: "Google Ads manager id",
    placeholder: "Leave empty if there is no manager account",
    help: "Only when the ads account sits under a manager account and the service account was added there.",
  },
  {
    key: "metaAdAccountId",
    label: "Meta ad account id",
    placeholder: "998877665544",
    help: "In Meta Ads Manager, the digits after act_ in the address.",
  },
  {
    key: "facebookPageId",
    label: "Facebook Page id",
    placeholder: "105555555555555",
    help: "On the Page, About, Page transparency. Instagram posts go to the Instagram account linked to this Page.",
  },
];

/** Each company's own Google and Meta account ids. HQ's keys are in Settings, Keys; values set at HQ pass down. */
function LinkedAccounts() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  const [draft, setDraft] = useState<Linked | null>(null);
  if (!s.data) return null;
  const m = s.data.settings.marketing;
  const owner = can("settings.update");
  return (
    <Card
      id="linked"
      title="Linked accounts"
      action={
        owner && !draft ? (
          <Button
            type="secondary"
            size="small"
            icon="edit"
            onClick={() =>
              setDraft({
                googleUser: m.googleUser,
                googleAdsId: m.googleAdsId,
                googleAdsManagerId: m.googleAdsManagerId,
                metaAdAccountId: m.metaAdAccountId,
                facebookPageId: m.facebookPageId,
              })
            }
          >
            Edit
          </Button>
        ) : null
      }
      flush={!draft}
    >
      {draft ? (
        <div className="stack">
          {LINKED_FIELDS.map((f) => (
            <InputText
              key={f.key}
              label={f.label}
              wide
              placeholder={f.placeholder}
              value={draft[f.key]}
              description={f.help}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
            />
          ))}
          <div className="row end">
            <Button type="tertiary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  {
                    marketing: {
                      googleUser: draft.googleUser.trim(),
                      googleAdsId: draft.googleAdsId.trim(),
                      googleAdsManagerId: draft.googleAdsManagerId.trim(),
                      metaAdAccountId: draft.metaAdAccountId.trim().replace(/^act_/, ""),
                      facebookPageId: draft.facebookPageId.trim(),
                    },
                  },
                  {
                    onSuccess: () => {
                      setDraft(null);
                      toast("Saved");
                    },
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  },
                )
              }
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <Details rows={LINKED_FIELDS.map((f) => [f.label, m[f.key] || null])} />
      )}
    </Card>
  );
}

/** Posting has its own switch (answer 111), and 4 and 5 star reviews can get replies on their own (answer 110). */
export function PostingSwitches() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  const m = s.data?.settings.marketing;
  if (!m || !can("settings.read")) return null;
  const set = (patch: { postingLive?: boolean; autoReply?: boolean }, msg: string) =>
    save.mutate({ marketing: patch }, { onSuccess: () => toast(msg), onError: (e) => toast(errText(e), { kind: "error" }) });
  const owner = can("settings.update");
  return (
    <Card id="posting" title="Posting and review replies">
      <SettingRow
        title="Post to Google and Facebook"
        description="Approved replies and posts go out. Texts and emails have their own switch."
        control={
          <Switch
            checked={m.postingLive}
            disabled={!owner}
            label="Post to Google and Facebook"
            onChange={(v) => set({ postingLive: v }, v ? "Posting on" : "Posting off")}
          />
        }
      />
      <SettingRow
        title="Reply to 4 and 5 star reviews on their own"
        description="New good reviews get a thank you reply. Lower ratings wait for you."
        control={
          <Switch
            checked={m.autoReply}
            disabled={!owner}
            label="Reply to good reviews on their own"
            onChange={(v) => set({ autoReply: v }, v ? "Auto replies on" : "Auto replies off")}
          />
        }
      />
    </Card>
  );
}

export function ReviewsSettings() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  const [draft, setDraft] = useState<{ reviewUrl: string; referral: string } | null>(null);
  if (!s.data) return <Skeleton rows={4} />;
  const m = s.data.settings.marketing;
  const owner = can("settings.update");
  const badUrl = draft !== null && draft.reviewUrl.trim() !== "" && !/^https:\/\/\S+$/.test(draft.reviewUrl.trim());
  return (
    <>
      <Card
        id="reviews"
        title="Reviews and referrals"
        action={
          owner && !draft ? (
            <Button
              type="secondary"
              size="small"
              icon="edit"
              onClick={() => setDraft({ reviewUrl: m.reviewUrl, referral: (m.referralCents / 100).toFixed(2) })}
            >
              Edit
            </Button>
          ) : null
        }
        flush={!draft}
      >
        {draft ? (
          <div className="stack">
            <InputText
              label="Google review link"
              type="url"
              wide
              placeholder="https://g.page/r/..."
              value={draft.reviewUrl}
              error={badUrl ? "Start with https://" : null}
              description="In your Google Business Profile, press Ask for reviews and copy the link."
              onChange={(e) => setDraft({ ...draft, reviewUrl: e.target.value })}
            />
            <InputText
              label="Referral reward"
              prefix="$"
              inputMode="decimal"
              value={draft.referral}
              description="Both people get this off their next invoice when a friend they sent books."
              onChange={(e) => setDraft({ ...draft, referral: e.target.value })}
            />
            <div className="row end">
              <Button type="tertiary" onClick={() => setDraft(null)}>
                Cancel
              </Button>
              <Button
                loading={save.isPending}
                disabled={badUrl}
                onClick={() =>
                  save.mutate(
                    {
                      marketing: {
                        reviewUrl: draft.reviewUrl.trim(),
                        referralCents: Math.max(0, Math.min(100_000, toCents(Number(draft.referral) || 0))),
                      },
                    },
                    {
                      onSuccess: () => {
                        setDraft(null);
                        toast("Saved");
                      },
                      onError: (e) => toast(errText(e), { kind: "error" }),
                    },
                  )
                }
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <Details
            rows={[
              ["Google review link", m.reviewUrl || null],
              ["Referral reward", `${formatMoney(m.referralCents as Cents)} for each of them`],
            ]}
          />
        )}
      </Card>
      {!m.reviewUrl ? <Banner type="warning">Review requests stay off until the Google review link is set.</Banner> : null}
      <LinkedAccounts />
      <PostingSwitches />
      <p className="muted">
        Review requests and referral offers are automatic messages with their own switch.{" "}
        <Link to="/settings/automations">Open automatic messages</Link>
      </p>
    </>
  );
}
